import { useUIStore } from "@/stores";
import { REGEX } from "@/utils";
import { Box, Button, Stack, TextField } from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { z } from "zod";
import { useSetReferralPaymentSettings } from "../../queries";
import { SetPaymentSettingsProps } from "./set-payment-settings.types";

const initialFormState = {
  museReferralPaymentCents: "",
  alphaReferralPaymentBps: "",
  bankAccountCredentials: "",
};

const formSchema = z.object({
  museReferralPaymentCents: z
    .string()
    .min(1, "Field is required")
    .regex(/^\d{1,3}$/, "Max 3 digits"),
  alphaReferralPaymentBps: z
    .string()
    .min(1, "Field is required")
    .regex(/^\d{1,3}$/, "Max 3 digits"),
  bankAccountCredentials: z
    .string()
    .min(1, "Field is required")
    .max(500, "Max 500 characters"),
});

type FormData = z.infer<typeof formSchema>;

const SetPaymentSettings: FC<SetPaymentSettingsProps> = ({ referralId }) => {
  const { closeModal } = useUIStore();
  const { setReferralPaymentSettings, isSetReferralPaymentSettingsLoading } =
    useSetReferralPaymentSettings();

  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
    museReferralPaymentCents: false,
    alphaReferralPaymentBps: false,
    bankAccountCredentials: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {}
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setReferralPaymentSettings({
      referralId,
      ...formData,
      museReferralPaymentCents: +formData.museReferralPaymentCents * 100,
      alphaReferralPaymentBps: +formData.alphaReferralPaymentBps * 100,
    });
  };
  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box component={"form"} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <TextField
          fullWidth
          required
          placeholder="Woman Referral Payment ($)"
          variant="outlined"
          label={
            touched.museReferralPaymentCents && errors?.museReferralPaymentCents
              ? errors?.museReferralPaymentCents?._errors[0]
              : "Woman Referral Payment ($)"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, museReferralPaymentCents: true }))
          }
          error={
            touched.museReferralPaymentCents
              ? !!errors?.museReferralPaymentCents?._errors.length
              : false
          }
          value={formData.museReferralPaymentCents}
          onChange={(e) => {
            const { value } = e.target;

            if (!REGEX.ONLY_NUMBERS.test(e.target.value) && value.length !== 0)
              return;

            setAdminUserFormData((prev) => ({
              ...prev,
              museReferralPaymentCents: e.target.value,
            }));
            if (touched.museReferralPaymentCents) {
              validate();
            }
          }}
        />
        <TextField
          fullWidth
          placeholder="Man Referral Payment (%)"
          required
          variant="outlined"
          label={
            touched.alphaReferralPaymentBps && errors?.alphaReferralPaymentBps
              ? errors?.alphaReferralPaymentBps?._errors[0]
              : "Man Referral Payment (%)"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, alphaReferralPaymentBps: true }))
          }
          error={
            touched.alphaReferralPaymentBps
              ? !!errors?.alphaReferralPaymentBps?._errors.length
              : false
          }
          value={formData.alphaReferralPaymentBps}
          onChange={(e) => {
            const { value } = e.target;

            if (value === "") {
              setAdminUserFormData((prev) => ({
                ...prev,
                alphaReferralPaymentBps: "",
              }));
              return;
            }

            if (
              !REGEX.ONLY_NUMBERS.test(e.target.value) ||
              Number(value) > 100
            ) {
              return;
            }

            setAdminUserFormData((prev) => ({
              ...prev,
              alphaReferralPaymentBps: e.target.value,
            }));
            if (touched.alphaReferralPaymentBps) {
              validate();
            }
          }}
        />
        <TextField
          multiline
          placeholder="Bank account credentials"
          slotProps={{ htmlInput: { maxLength: 500 } }}
          minRows={5}
          required
          variant="outlined"
          label={
            touched.bankAccountCredentials && errors?.bankAccountCredentials
              ? errors?.bankAccountCredentials?._errors[0]
              : "Bank account credentials"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, bankAccountCredentials: true }))
          }
          error={
            touched.bankAccountCredentials
              ? !!errors?.bankAccountCredentials?._errors.length
              : false
          }
          value={formData.bankAccountCredentials}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              bankAccountCredentials: e.target.value,
            }));
            if (touched.bankAccountCredentials) {
              validate();
            }
          }}
        />
      </Stack>

      <Stack direction={"row"} spacing={2} mt={2}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          loading={isSetReferralPaymentSettingsLoading}
          disabled={
            isSetReferralPaymentSettingsLoading || !isFormFilled || !!errors
          }
        >
          Update
        </Button>
      </Stack>
    </Box>
  );
};

export { SetPaymentSettings };
